import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Tooltip,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Email as EmailIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Replay as ReplayIcon,
  Dashboard as DashboardIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import api from '../services/api';
import NewsletterTabs from '../components/molecules/NewsletterTabs/NewsletterTabs';

const NewsletterDashboard = () => {
  const navigate = useNavigate();
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalNewsletters: 0,
    totalSent: 0,
    totalFailed: 0,
    totalRecipients: 0,
    openRate: 0,
    clickRate: 0
  });
  const [selectedNewsletter, setSelectedNewsletter] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [titleSearch, setTitleSearch] = useState('');
  const [sourceSearch, setSourceSearch] = useState('');

  useEffect(() => {
    loadNewsletters();
    loadStats();
  }, []);

  const handleResend = (newsletter) => {
    navigate('/newsletter', {
      state: {
        selectedArticleIds: newsletter.articles.map(a => a._id || a),
        resendNewsletter: {
          subject: `Encaminhado: ${newsletter.subject}`,
          message: newsletter.message,
          templateId: newsletter.template,
          recipients: newsletter.recipients,
          groups: newsletter.groups,
          groupByTags: newsletter.groupByTags,
          showImages: newsletter.showImages
        }
      }
    });
  };

  const loadNewsletters = async () => {
    try {
      setLoading(true);
      const response = await api.get('/newsletter');
      setNewsletters(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar newsletters:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/newsletter/stats');
      setStats(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const handleViewDetails = (newsletter) => {
    setSelectedNewsletter(newsletter);
    setDetailsDialogOpen(true);
  };

  const handleDeleteNewsletter = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta newsletter?')) {
      try {
        await api.delete(`/newsletter/${id}`);
        loadNewsletters();
      } catch (error) {
        console.error('Erro ao excluir newsletter:', error);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'success';
      case 'sending': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent': return <CheckCircleIcon />;
      case 'sending': return <SendIcon />;
      case 'failed': return <ErrorIcon />;
      default: return <EmailIcon />;
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('pt-BR');
  };

  const formatPublicationDate = (date) => {
    if (!date) return 'Não informada';
    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) return 'Não informada';
    return parsedDate.toLocaleDateString('pt-BR');
  };

  const normalizeSearchValue = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
    .trim();

  const normalizedTitleSearch = normalizeSearchValue(titleSearch);
  const normalizedSourceSearch = normalizeSearchValue(sourceSearch);
  const hasActiveFilters = Boolean(normalizedTitleSearch || normalizedSourceSearch);
  const filteredNewsletters = newsletters.filter((newsletter) => {
    const articles = Array.isArray(newsletter.articles) ? newsletter.articles : [];
    const matchesTitle = !normalizedTitleSearch || [
      newsletter.subject,
      ...articles.map((article) => article?.title)
    ].some((value) => normalizeSearchValue(value).includes(normalizedTitleSearch));
    const matchesSource = !normalizedSourceSearch || articles.some((article) => (
      normalizeSearchValue(article?.siteName).includes(normalizedSourceSearch)
    ));

    return matchesTitle && matchesSource;
  });

  const clearSearchFilters = () => {
    setTitleSearch('');
    setSourceSearch('');
  };

  const StatCard = ({ title, value, icon, color = 'primary' }) => (
    <Card elevation={0} sx={{ height: '100%', borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 2 } }}>
      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {icon}
          <Typography variant="h6" sx={{ ml: 1 }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" color={`${color}.main`}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1, m: 0, fontWeight: 700, color: 'text.primary' }}>
            <DashboardIcon color="primary" />
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 4.5, mb: 3 }}>
            Acompanhe o desempenho e métricas dos seus envios de newsletter.
          </Typography>
        </Box>
      </Box>
      <NewsletterTabs />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={() => {
            loadNewsletters();
            loadStats();
          }}
        >
          Atualizar
        </Button>
      </Box>

      {/* Estatísticas Gerais */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            title="Total"
            value={stats.totalNewsletters}
            icon={<EmailIcon color="primary" />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            title="Enviadas"
            value={stats.totalSent}
            icon={<CheckCircleIcon color="success" />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            title="Falharam"
            value={stats.totalFailed}
            icon={<ErrorIcon color="error" />}
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            title="Destinatários"
            value={stats.totalRecipients}
            icon={<PeopleIcon color="info" />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            title="Taxa Abertura"
            value={`${stats.openRate}%`}
            icon={<VisibilityIcon color="warning" />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            title="Taxa Cliques"
            value={`${stats.clickRate}%`}
            icon={<TrendingUpIcon color="secondary" />}
            color="secondary"
          />
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ px: 1 }}>
          Histórico de Envios
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Título ou assunto"
            placeholder="Pesquise pelo título da notícia"
            value={titleSearch}
            onChange={(event) => setTitleSearch(event.target.value)}
            sx={{ flex: '1 1 280px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />
          <TextField
            size="small"
            label="Fonte"
            placeholder="Ex.: G1, Folha de S.Paulo"
            value={sourceSearch}
            onChange={(event) => setSourceSearch(event.target.value)}
            sx={{ flex: '1 1 240px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />
          {hasActiveFilters && (
            <Button startIcon={<ClearIcon />} onClick={clearSearchFilters} color="inherit">
              Limpar
            </Button>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ ml: { md: 'auto' } }}>
            {filteredNewsletters.length} de {newsletters.length} envio(s)
          </Typography>
        </Box>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Assunto</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Destinatários</TableCell>
                <TableCell>Enviados</TableCell>
                <TableCell>Falharam</TableCell>
                <TableCell>Data</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredNewsletters.map((newsletter) => (
                <TableRow key={newsletter._id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {newsletter.subject}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {newsletter.articles?.length || 0} artigo(s)
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(newsletter.status)}
                      label={newsletter.status}
                      color={getStatusColor(newsletter.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{newsletter.totalRecipients}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ mr: 1 }}>
                        {newsletter.sentCount}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(newsletter.sentCount / newsletter.totalRecipients) * 100}
                        sx={{ width: 50, height: 6 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>{newsletter.failedCount}</TableCell>
                  <TableCell>
                    {newsletter.sentAt ? formatDate(newsletter.sentAt) : formatDate(newsletter.createdAt)}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Visualizar Detalhes">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(newsletter)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reenviar Newsletter">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleResend(newsletter)}
                      >
                        <ReplayIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteNewsletter(newsletter._id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && filteredNewsletters.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters
                        ? 'Nenhum envio encontrado para os filtros informados.'
                        : 'Nenhum envio de newsletter encontrado.'}
                    </Typography>
                    {hasActiveFilters && (
                      <Button size="small" onClick={clearSearchFilters} sx={{ mt: 1 }}>
                        Limpar filtros
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog de Detalhes */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, backgroundImage: 'none', p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider', pb: 2, mb: 2 }}>Detalhes do Envio</DialogTitle>
        <DialogContent sx={{ py: 0 }}>
          {selectedNewsletter && (
            <Box>
              <Typography variant="h6" gutterBottom color="primary.main" fontWeight={700}>
                {selectedNewsletter.subject}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {selectedNewsletter.message}
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Estatísticas:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Total de Destinatários:</strong> {selectedNewsletter.totalRecipients}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Enviados com Sucesso:</strong> {selectedNewsletter.sentCount}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Falharam:</strong> {selectedNewsletter.failedCount}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Taxa de Sucesso:</strong> {((selectedNewsletter.sentCount / selectedNewsletter.totalRecipients) * 100).toFixed(1)}%
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Artigos Incluídos:
                </Typography>
                {selectedNewsletter.articles?.map((article, index) => (
                  <Card key={index} sx={{ mb: 1 }}>
                    <CardContent sx={{ py: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {article.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary">
                          <strong>Fonte:</strong> {article.siteName || 'Não informada'}
                        </Typography>
                        {article.mediaType && (
                          <>
                            <Typography variant="caption" color="text.disabled">•</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {article.mediaType}
                            </Typography>
                          </>
                        )}
                        {article.siteUrl && (
                          <>
                            <Typography variant="caption" color="text.disabled">•</Typography>
                            <Typography
                              component="a"
                              href={article.siteUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="caption"
                              color="primary.main"
                              sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                              Acessar fonte
                            </Typography>
                          </>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        <strong>Publicação:</strong> {formatPublicationDate(article.date)}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                          Tags:
                        </Typography>
                        {article.tags?.length ? article.tags.map((tag, tagIndex) => (
                          <Chip
                            key={tag?._id || `${tag?.name || tag}-${tagIndex}`}
                            label={tag?.name || tag}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 22,
                              fontSize: '0.68rem',
                              ...(tag?.color && {
                                borderColor: tag.color,
                                color: tag.color
                              })
                            }}
                          />
                        )) : (
                          <Typography variant="caption" color="text.disabled">
                            Nenhuma tag
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default NewsletterDashboard;
